import { Link } from "react-router-dom";
import type { PersonaLink } from "./personaLinks";
import type { ResolvedPersona } from "./types";
import type { Variant } from "./store";

/**
 * Shared 4-up grid of persona links, used by both the homepage spotlight and the
 * site-wide next-steps band. Each link is tagged (persona + A/B variant) so click-through
 * — and therefore lift over the control group — is measurable in cookieless analytics.
 */
export function PersonaLinkGrid({
  heading,
  links,
  event,
  persona,
  variant,
  className = "",
}: {
  heading: string;
  links: PersonaLink[];
  event: string;
  persona: ResolvedPersona;
  variant: Variant;
  className?: string;
}) {
  return (
    <section className={`relative section-x ${className}`} aria-label={heading}>
      <div className="relative max-w-5xl mx-auto">
        <p className="text-slate-300 text-xs tracking-widest uppercase mb-6 select-none">
          {heading}
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {links.map((link) => {
            const attrs = {
              "data-umami-event": event,
              "data-umami-event-persona": persona,
              "data-umami-event-variant": variant,
              "data-umami-event-label": link.label,
            };
            const inner = (
              <>
                <span className="text-white font-medium group-hover:text-gold-400 transition-colors">
                  {link.label}
                </span>
                <span className="mt-1 block text-sm text-slate-400 leading-relaxed">
                  {link.desc}
                </span>
              </>
            );
            const cls =
              "group block rounded-xl border border-white/10 p-4 transition-colors hover:border-gold/30";
            return link.href.startsWith("http") ? (
              <a key={link.href} href={link.href} className={cls} {...attrs}>
                {inner}
              </a>
            ) : (
              <Link key={link.href} to={link.href} className={cls} {...attrs}>
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
